<template>
  <div
    ref="rootRef"
    class="region-multi"
    :class="{ 'is-open': open }"
  >
    <button
      ref="triggerRef"
      type="button"
      class="region-multi__trigger"
      :class="{ 'is-active': hasSelection }"
      aria-haspopup="listbox"
      :aria-expanded="open"
      @click="toggle"
    >
      <i class="fas fa-map-marker-alt region-multi__pin" aria-hidden="true" />
      <span class="region-multi__text">{{ appliedLabel }}</span>
      <span class="region-multi__caret" aria-hidden="true" />
    </button>

    <div v-if="open" class="region-multi__panel" role="listbox" :style="panelStyle">
      <div class="region-multi__body">
        <!-- 左：地方。行クリックで右カラムに都道府県を出し、チェックで地方まるごと選択 -->
        <div class="region-multi__col region-multi__col--area">
          <button
            type="button"
            class="region-multi__item region-multi__item--nation"
            :class="{ 'is-selected': !hasDraft }"
            @click="pickNationwide"
          >
            <span class="region-multi__item-name">全国</span>
          </button>
          <!-- 近隣：所属劇場を中心にした半径絞り込み。地方・都道府県とは排他（選ぶと両方クリアされる）。
               所属劇場が未設定のユーザーは中心点が無いので選べない（置灰ではなく非表示にする） -->
          <button
            v-if="nearbyAvailable"
            type="button"
            class="region-multi__item region-multi__item--nearby"
            :class="{ 'is-selected': draftNearby }"
            @click="pickNearby"
          >
            <span class="region-multi__item-name">{{ NEARBY_LABEL }}</span>
          </button>
          <div
            v-for="area in areaOptions"
            :key="`area-${area.id}`"
            class="region-multi__item"
            :class="{
              'is-active': focusedAreaId === area.id,
              'is-checked': draftAreaIds.includes(area.id),
            }"
            @click="focusArea(area)"
          >
            <label class="region-multi__check" @click.stop>
              <input
                type="checkbox"
                :checked="draftAreaIds.includes(area.id)"
                :indeterminate.prop="isAreaPartial(area.id)"
                @change="toggleAreaWithFocus(area)"
              >
              <span
                class="region-multi__box"
                :class="{ 'is-partial': isAreaPartial(area.id) }"
                aria-hidden="true"
              />
            </label>
            <span class="region-multi__item-name">{{ area.name }}</span>
            <span v-if="prefCountOfArea(area.id)" class="region-multi__badge">
              {{ prefCountOfArea(area.id) }}
            </span>
            <span class="region-multi__arrow" aria-hidden="true" />
          </div>
        </div>

        <!-- 右：都道府県。親の地方と同時に選べる（バックエンドは OR 集合として扱う） -->
        <div class="region-multi__col region-multi__col--pref">
          <div v-if="focusedAreaId == null" class="region-multi__hint">
            地方を選ぶと都道府県が表示されます
          </div>
          <div v-else-if="prefectureLoading" class="region-multi__hint">読込中…</div>
          <template v-else>
            <label
              v-for="pref in prefectureOptions"
              :key="`pref-${pref.id}`"
              class="region-multi__item region-multi__item--pref"
              :class="{ 'is-checked': isPrefChecked(pref) }"
            >
              <span class="region-multi__check">
                <input
                  type="checkbox"
                  :checked="isPrefChecked(pref)"
                  @change="togglePrefecture(pref)"
                >
                <span class="region-multi__box" aria-hidden="true" />
              </span>
              <span class="region-multi__item-name">{{ pref.name }}</span>
            </label>
          </template>
        </div>
      </div>

      <div class="region-multi__footer">
        <span class="region-multi__draft" :title="draftLabel">{{ draftLabel }}</span>
        <div class="region-multi__actions">
          <button
            type="button"
            class="region-multi__clear"
            :disabled="!hasDraft"
            @click="pickNationwide"
          >
            クリア
          </button>
          <button type="button" class="region-multi__confirm" @click="confirm">
            適用
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { getAreaListService, getPrefectureListService } from '@/api/dp/index.js'
import useUserStore from '@/store/modules/user'

/** movie-content 側と揃えたスマホ判定の閾値 */
const MOBILE_BREAKPOINT_MQ = '(max-width: 768px)'
/** スマホでパネルを固定配置する際の画面左右の余白 */
const PANEL_EDGE_GAP = 10
/**
 * 地方・都道府県を一度に取り切るためのページサイズ。
 * バックエンドは startPage() の既定が pageSize=10 なので、
 * 指定しないと 10 件で打ち切られる（地方 8 件・都道府県 47 件）
 */
const FULL_PAGE_SIZE = 500

/** 近隣絞り込みの表示名。半径はバックエンド（NearbyCinemaService.RADIUS_KM）と揃えること */
const NEARBY_LABEL = '近隣50km'

function isMobileViewport() {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_BREAKPOINT_MQ).matches
}

const props = defineProps({
  /** 適用済みの地方ID配列 */
  areaIds: {
    type: Array,
    default: () => [],
  },
  /** 適用済みの都道府県ID配列。areaIds とは OR（和集合）の関係で、同時指定・親子混在も可 */
  prefectureIds: {
    type: Array,
    default: () => [],
  },
  /** 適用済みの「近隣」絞り込み。true のとき areaIds/prefectureIds は必ず空（排他） */
  nearby: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['confirm'])

const userStore = useUserStore()
/** 近隣絞り込みの可否：中心点は所属劇場なので、未設定のユーザーは選べない */
const nearbyAvailable = computed(() => Boolean(userStore.cinemaId))

const rootRef = ref(null)
const triggerRef = ref(null)
const open = ref(false)
/** スマホ時のみ設定するパネルの固定配置スタイル（PC は空＝CSS の absolute のまま） */
const panelStyle = ref({})
const areaOptions = ref([])
const prefectureOptions = ref([])
const prefectureLoading = ref(false)
const focusedAreaId = ref(null)
/** 地方ID -> 配下の都道府県配列。初期化時に全件分を作る */
const prefectureCache = new Map()
/** 都道府県ID -> 所属地方ID。ラベル生成とバッジ表示に使う */
const prefectureAreaMap = new Map()
/** 都道府県ID -> 名称。他の地方を開いた後でもラベルを出せるように保持 */
const prefectureNameMap = new Map()

/** パネル内の下書き。「適用」を押すまで確定しない */
const draftAreaIds = ref([])
const draftPrefectureIds = ref([])
const draftNearby = ref(false)

const hasDraft = computed(
  () => draftAreaIds.value.length > 0 || draftPrefectureIds.value.length > 0 || draftNearby.value,
)
const hasSelection = computed(
  () => props.areaIds.length > 0 || props.prefectureIds.length > 0 || props.nearby,
)

const appliedLabel = computed(() => buildLabel(props.areaIds, props.prefectureIds, props.nearby))
const draftLabel = computed(() => buildLabel(draftAreaIds.value, draftPrefectureIds.value, draftNearby.value))

/** その地方に属する選択済み都道府県の数（左カラムのバッジ用） */
function prefCountOfArea(areaId) {
  return draftPrefectureIds.value.filter((id) => prefectureAreaMap.get(id) === areaId).length
}

/**
 * 表示ラベル。全て未選択なら「全国」、1件ならその名称、
 * 複数なら「◯◯ 他N件」に畳む（トリガーの幅が限られるため）
 */
function buildLabel(areaIds, prefectureIds, nearby) {
  if (nearby) return NEARBY_LABEL
  const names = []
  areaIds.forEach((id) => {
    const hit = areaOptions.value.find((item) => item.id === id)
    if (hit) names.push(hit.name)
  })
  prefectureIds.forEach((id) => {
    const name = prefectureNameMap.get(id)
    if (name) names.push(name)
  })
  if (!names.length) return '全国'
  if (names.length === 1) return names[0]
  return `${names[0]} 他${names.length - 1}件`
}

function syncDraftFromProps() {
  draftAreaIds.value = [...props.areaIds]
  draftPrefectureIds.value = [...props.prefectureIds]
  draftNearby.value = props.nearby
  // 適用済みの都道府県が属する地方を開いておくと、開き直したときに文脈が分かる
  const firstPrefAreaId = props.prefectureIds.length
    ? prefectureAreaMap.get(props.prefectureIds[0])
    : null
  const nextFocus = firstPrefAreaId ?? props.areaIds[0] ?? null
  focusedAreaId.value = nextFocus
  showPrefecturesOfArea(nextFocus)
}

/**
 * スマホではパネルを画面幅いっぱいに固定配置する。
 * トリガーがヘッダー中央寄りにあると absolute（left:0）では右カラム（都道府県）が
 * 画面外にはみ出して見えなくなるため、viewport 基準で左右に余白を取る
 */
function updatePanelPosition() {
  if (!isMobileViewport()) {
    panelStyle.value = {}
    return
  }
  const trigger = triggerRef.value
  if (!trigger) return
  const rect = trigger.getBoundingClientRect()
  panelStyle.value = {
    position: 'fixed',
    top: `${Math.round(rect.bottom + 6)}px`,
    left: `${PANEL_EDGE_GAP}px`,
    right: `${PANEL_EDGE_GAP}px`,
    width: 'auto',
    // PC 用の translateX(-50%)（left:50% 中央寄せ）を打ち消す。
    // 残したままだとパネルが幅の半分だけ左にずれて画面外に出る
    transform: 'none',
  }
}

function setOpen(value) {
  open.value = value
  if (value) {
    syncDraftFromProps()
    void nextTick(updatePanelPosition)
  }
}

function toggle() {
  setOpen(!open.value)
}

function close() {
  if (open.value) setOpen(false)
}

function focusArea(area) {
  focusedAreaId.value = area.id
  showPrefecturesOfArea(area.id)
}

/**
 * 都道府県のチェック状態。地方がチェックされていれば配下は暗黙的に全選択とみなす。
 * （地方 ID だけを送ればバックエンドは配下すべてを対象にするため、
 *   個別 ID に展開せず畳んだまま保持する）
 */
function isPrefChecked(pref) {
  const areaId = prefectureAreaMap.get(pref.id) ?? focusedAreaId.value
  return draftAreaIds.value.includes(areaId) || draftPrefectureIds.value.includes(pref.id)
}

/** 配下の一部だけ選ばれている状態（地方チェックボックスを中間表示にする） */
function isAreaPartial(areaId) {
  if (draftAreaIds.value.includes(areaId)) return false
  return draftPrefectureIds.value.some((id) => prefectureAreaMap.get(id) === areaId)
}

/** 一次エリアのチェックは配下の都道府県と連動させる（外す時は配下の個別指定も消す） */
function toggleArea(areaId) {
  // 地方を触った時点で近隣モードは解除（両者は排他）
  draftNearby.value = false
  const dropChildren = draftPrefectureIds.value.filter(
    (id) => prefectureAreaMap.get(id) !== areaId,
  )
  if (draftAreaIds.value.includes(areaId)) {
    draftAreaIds.value = draftAreaIds.value.filter((id) => id !== areaId)
  } else {
    draftAreaIds.value = [...draftAreaIds.value, areaId]
  }
  // 地方を入れた場合は配下が暗黙的に全選択になるので個別指定は不要、
  // 外した場合は配下も一緒に解除する。どちらも「配下を落とす」で足りる
  draftPrefectureIds.value = dropChildren
}

/**
 * 一次エリアのチェックボックス操作。選択状態の切り替えに加えて、
 * 右カラムもその地方に切り替える（チェック時・解除時のどちら
 * でも）。チェックだけして右が変わらないと、何が選ばれたのか
 * 分からず、続けて都道府県を個別に選ぶこともできないため
 */
function toggleAreaWithFocus(area) {
  toggleArea(area.id)
  focusArea(area)
}

function togglePrefecture(pref) {
  // 都道府県を触った時点で近隣モードは解除（両者は排他）
  draftNearby.value = false
  const areaId = prefectureAreaMap.get(pref.id) ?? focusedAreaId.value
  prefectureAreaMap.set(pref.id, areaId)
  prefectureNameMap.set(pref.id, pref.name)
  const siblings = prefectureCache.get(areaId) ?? []

  // 地方チェックによる暗黙全選択から 1 県だけ外す → 地方を個別指定に展開してから外す
  if (draftAreaIds.value.includes(areaId)) {
    draftAreaIds.value = draftAreaIds.value.filter((id) => id !== areaId)
    const rest = siblings.filter((item) => item.id !== pref.id).map((item) => item.id)
    draftPrefectureIds.value = [...new Set([...draftPrefectureIds.value, ...rest])]
    return
  }

  const next = draftPrefectureIds.value.includes(pref.id)
    ? draftPrefectureIds.value.filter((id) => id !== pref.id)
    : [...draftPrefectureIds.value, pref.id]

  // 配下が全て揃ったら地方チェックに畳む（二次エリア全選択 → 一次エリアもチェック）
  const isAllChecked = siblings.length > 0 && siblings.every((item) => next.includes(item.id))
  if (isAllChecked) {
    draftAreaIds.value = [...draftAreaIds.value, areaId]
    draftPrefectureIds.value = next.filter((id) => prefectureAreaMap.get(id) !== areaId)
  } else {
    draftPrefectureIds.value = next
  }
}

function pickNationwide() {
  draftAreaIds.value = []
  draftPrefectureIds.value = []
  draftNearby.value = false
}

/** 近隣モードへ切り替え。地方・都道府県の選択は捨てる（サーバー側も両者を同時には受け付けない） */
function pickNearby() {
  if (!nearbyAvailable.value) return
  draftAreaIds.value = []
  draftPrefectureIds.value = []
  draftNearby.value = true
}

function confirm() {
  emit('confirm', {
    areaIds: [...draftAreaIds.value],
    prefectureIds: [...draftPrefectureIds.value],
    nearby: draftNearby.value,
    label: draftLabel.value,
  })
  setOpen(false)
}

async function loadAreaOptions() {
  try {
    const res = await getAreaListService({ pageNum: 1, pageSize: FULL_PAGE_SIZE })
    areaOptions.value = (res?.rows || []).map((item) => ({ id: item.id, name: item.name }))
  } catch (error) {
    console.error('[dashboard] getAreaListService failed', error)
    areaOptions.value = []
  }
}

/**
 * 都道府県を全件まとめて取得し、地方ごとに振り分けてキャッシュする。
 * areaId 未指定なら全件返る仕様で、レスポンスは area_id・sort の昇順に
 * 並んでいるため、そのまま順に詰めれば地方ごとの表示順になる。
 * 地方を開くたびに都度リクエストしていたのをやめ、初期化時の 1 回に集約する
 */
async function loadAllPrefectures() {
  prefectureLoading.value = true
  try {
    const res = await getPrefectureListService({ pageNum: 1, pageSize: FULL_PAGE_SIZE })
    prefectureCache.clear()
    ;(res?.rows || []).forEach((item) => {
      const row = { id: item.id, name: item.name }
      prefectureAreaMap.set(row.id, item.areaId)
      prefectureNameMap.set(row.id, row.name)
      if (!prefectureCache.has(item.areaId)) prefectureCache.set(item.areaId, [])
      prefectureCache.get(item.areaId).push(row)
    })
  } catch (error) {
    console.error('[dashboard] getPrefectureListService failed', error)
    prefectureCache.clear()
  } finally {
    prefectureLoading.value = false
    // 取得前に地方が選ばれていた場合（初期化中に開かれた等）は右カラムを反映し直す
    showPrefecturesOfArea(focusedAreaId.value)
  }
}

/** 右カラムの表示切り替え。全件キャッシュ済みなので通信は発生しない */
function showPrefecturesOfArea(areaId) {
  prefectureOptions.value = areaId == null ? [] : prefectureCache.get(areaId) ?? []
}

function onPointerDownOutside(event) {
  if (!open.value) return
  const root = rootRef.value
  if (root && !root.contains(event.target)) close()
}

watch(
  () => [props.areaIds, props.prefectureIds, props.nearby],
  () => {
    if (open.value) syncDraftFromProps()
  },
)

function onViewportChange() {
  if (open.value) updatePanelPosition()
}

onMounted(() => {
  document.addEventListener('pointerdown', onPointerDownOutside, true)
  window.addEventListener('resize', onViewportChange)
  window.addEventListener('orientationchange', onViewportChange)
  // 地方・都道府県とも初期化時に全件取得しておく（以降は通信なしで切り替わる）
  void loadAreaOptions()
  void loadAllPrefectures()
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', onPointerDownOutside, true)
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('orientationchange', onViewportChange)
})

defineExpose({ close })
</script>

<style lang="less" scoped>
/* 見た目は master(prod) の region-picker.vue に揃える。
   複数選択のための要素（チェックボックス・バッジ・クリア）だけを足し、
   トリガー／パネル／項目の寸法・色は prod の値をそのまま使う */
.region-multi {
  position: relative;
  display: inline-flex;
  z-index: 12;
}

.region-multi__trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 160px;
  padding: 4px 10px;
  border: 1px solid var(--mc-border, #e5e5e5);
  border-radius: 14px;
  background: color-mix(in srgb, var(--mc-accent, #f9da49) 18%, var(--mc-bg, #fff));
  color: var(--mc-text, #000);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;

  &.is-active {
    background: color-mix(in srgb, var(--mc-accent, #f9da49) 28%, var(--mc-bg, #fff));
  }
}

.region-multi__pin {
  flex: none;
  font-size: 11px;
  opacity: 0.7;
}

.region-multi__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.region-multi__caret {
  flex: none;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid currentColor;
  opacity: 0.7;
  transition: transform 0.15s ease;
}

.region-multi.is-open .region-multi__caret {
  transform: rotate(180deg);
}

.region-multi__panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-width: 240px;
  max-height: min(400px, 60vh);
  overflow: hidden;
  border: 1px solid var(--mc-border, #e5e5e5);
  border-radius: 10px;
  background: var(--mc-bg, #fff);
  box-shadow: var(--shadow-sm, 0 8px 24px rgba(0, 0, 0, 0.12));
  /* 基準行高：.dashboard-header(line-height:56px) から継承される行高を断ち切る。
     これが無いと項目が 70px まで伸び、フッターのボタンも文字がずれる（prod と同じ対処） */
  line-height: 1.4;
  transform: translateX(-50%);
}

.region-multi__body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.region-multi__col {
  display: flex;
  flex-direction: column;
  min-width: 108px;
  max-height: min(300px, 48vh);
  overflow-y: auto;
  /* prod は .mc-scrollbar() mixin を使っているが、あれは movie-content.vue の
     scoped style 内で定義されたローカル mixin でここからは参照できない */
  scrollbar-width: thin;

  &--area {
    border-right: 1px solid var(--mc-border, #e5e5e5);
    background: var(--mc-bg-soft, #fafafa);
  }

  &--pref {
    min-width: 120px;
    flex: 1;
  }
}

.region-multi__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 12px;
  border: 0;
  background: transparent;
  color: var(--mc-text, #000);
  font-size: 12px;
  font-weight: 500;
  /* 項目は <button> / <label>。element-plus の reset で行高を継承するため、
     ここでも明示して .dashboard-header からの継承を断つ（prod と同じ対処） */
  line-height: 1.4;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: var(--mc-row-hover, #f5f5f5);
  }

  &.is-active {
    background: var(--mc-row-hover, #f5f5f5);
    font-weight: 700;
  }

  &.is-checked,
  &.is-selected {
    background: var(--mc-row-selected, #fdf3c9);
    font-weight: 700;
  }

  &--nation,
  &--nearby {
    justify-content: center;
  }
}

/* 「近隣」は全国と地方リストの境目なので、区切り線で分ける */
.region-multi__item--nearby {
  border-bottom: 1px solid var(--mc-border, #e5e5e5);
}

.region-multi__item-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 「全国」「近隣」はラベルを中央に置きたいので、flex:1 で伸ばさない */
.region-multi__item--nation .region-multi__item-name,
.region-multi__item--nearby .region-multi__item-name {
  flex: none;
}

.region-multi__badge {
  flex-shrink: 0;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--mc-accent, #f9da49);
  color: #1c1c1c;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
  font-weight: 700;
}

.region-multi__arrow {
  flex-shrink: 0;
  width: 5px;
  height: 5px;
  border-top: 1px solid currentColor;
  border-right: 1px solid currentColor;
  transform: rotate(45deg);
  opacity: 0.35;
}

.region-multi__check {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
}

.region-multi__box {
  position: relative;
  display: inline-block;
  box-sizing: border-box;
  width: 15px;
  height: 15px;
  border: 1px solid var(--mc-border, #c9c9c9);
  border-radius: 4px;
  background: var(--mc-bg, #fff);
  transition: background 0.15s ease, border-color 0.15s ease;
}

.region-multi__item:hover .region-multi__box {
  border-color: var(--mc-accent, #f9da49);
}

/*
 * チェックマークは「短辺と長辺の比が 1:2 の L 字」を 45 度回転して描く。
 * 2本の等長グラデーションで描くと線の長さが揃ってしまい、チェックではなく
 * 折れ線に見えてしまうため。translate の Y を -55% にしているのは、
 * 回転後の見た目の重心が幾何中心より下にくるぶんを戻すため
 */
.region-multi__check input:checked + .region-multi__box,
.region-multi__box.is-partial {
  border-color: var(--mc-accent, #f9da49);
  background: var(--mc-accent, #f9da49);
}

.region-multi__check input:checked + .region-multi__box::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  border-radius: 1px;
  transform: translate(-50%, -55%) rotate(45deg);
}

/* 配下の一部だけ選択中：チェックではなく横棒で中間状態を示す */
.region-multi__box.is-partial::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 2px;
  border-radius: 1px;
  background: #fff;
  transform: translate(-50%, -50%);
}

.region-multi__hint {
  padding: 10px 12px;
  color: var(--mc-text-muted, #8a8a8a);
  font-size: 12px;
}

.region-multi__footer {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border-top: 1px solid var(--mc-border, #e5e5e5);
  background: var(--mc-bg, #fff);
}

.region-multi__draft {
  min-width: 0;
  overflow: hidden;
  color: var(--mc-text-muted, #4a4a4a);
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.region-multi__actions {
  display: flex;
  flex: none;
  gap: 6px;
}

/* 高さは padding で作る（prod の .region-picker__confirm と同じ作り）。
   height 固定にすると継承した行高でラベルがずれる */
.region-multi__clear,
.region-multi__confirm {
  flex: none;
  min-width: 64px;
  padding: 6px 14px;
  border: 0;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;

  &:active {
    transform: translateY(1px);
  }
}

.region-multi__clear {
  border: 1px solid var(--mc-border, #e5e5e5);
  background: var(--mc-bg, #fff);
  color: var(--mc-text-muted, #4a4a4a);

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.region-multi__confirm {
  background: var(--brand-color, #f9da49);
  color: #fff; // 浅色红底用白字保证对比（prod と同じ）
  box-shadow: 0 1px 3px color-mix(in srgb, #000 12%, transparent);

  &:hover {
    filter: brightness(0.97);
  }
}

@media (max-width: 768px) {
  /* 位置と幅は updatePanelPosition() が inline style で指定する（fixed 配置） */
  .region-multi__panel {
    z-index: 2100;
    max-height: 70vh;
  }

  /* 二次エリア（都道府県）が見えるよう、左右のカラムを等分する */
  .region-multi__col {
    flex: 1;
    min-width: 0;
    max-height: 46vh;
  }
}
</style>
